import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

import { BankInformationResponse } from 'api/clients/PaymentClient/types';
import { PaymentClient } from 'api/clients/PaymentClient';
import { handleErrorMessage } from 'utils/errorHandler';
import { useQuery } from 'v3/hooks/useQuery';
import { useMixpanel } from '../v3/hooks/useMixpanel';
import { setLeadData } from 'leadData/leadSlice';
import { toast } from 'react-toastify';

export type CreditCardData = {
  name: string;
  number: string;
  cvc: string;
  date: string;
};

type ConnectCreditCardProps = {
  clubId: number;
  userUuid: string;
  cfUuid: string;
};

export const useConnectCreditCard = ({
  clubId,
  userUuid,
  cfUuid,
}: ConnectCreditCardProps) => {
  const query = useQuery();
  const name = query.get('name');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [paymentInformation, setPaymentInformation] =
    useState<BankInformationResponse>();
  const [loading, setLoading] = useState(false);

  const mp = useMixpanel();

  const handleValidateData = useCallback(
    async (creditCardData: CreditCardData) => {
      setLoading(true);

      const [data, error] = await PaymentClient().connectCreditCard({
        cfUuid,
        ...creditCardData,
      });

      if (error) {
        dispatch(setLeadData({ creditCardValidated: false }));
        const errorMessage = error?.response?.data?.error?.detail;
        if (errorMessage) {
          toast.error(
            `The transaction didn’t go through. ${errorMessage as string}`,
            {
              autoClose: false,
            }
          );
        } else {
          handleErrorMessage(error);
        }
      } else if (data) {
        mp.fireCreditCardSuccess();

        dispatch(setLeadData({ creditCardValidated: true }));

        if (name) {
          const nextPaymentDate = format(
            new Date(data.nextPaymentDate),
            'MM/dd/yy'
          );
          const paymentDate = format(new Date(data.paymentDate), 'MM/dd/yy');

          navigate(
            `/credit-card-success/?name=${name}&totalPaid=${String(
              data.totalAmount
            )}&paymentDate=${paymentDate}&nextPaymentDate=${nextPaymentDate}`
          );
        } else {
          dispatch(setLeadData({ currentStep: 5 }));
        }
      }

      setLoading(false);
    },
    [userUuid, clubId, name, paymentInformation, navigate, mp]
  );

  useEffect(() => {
    const loadPaymentData = async () => {
      const [data, error] = await PaymentClient().getUserPaymentInformation(
        cfUuid
      );

      if (data) {
        setPaymentInformation(data);
      } else if (error) {
        handleErrorMessage(error);
      }
    };

    if (clubId && userUuid) {
      loadPaymentData();
    }
  }, [clubId, userUuid]);

  return {
    handleValidateData,
    paymentInformation,
    loading,
  };
};
